// client/src/components/order/ConfirmDeliveryDate.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Calendar, Pencil, X, Clock } from 'lucide-react';
import { ordersAPI } from '../../services/api';
import ConfirmModal from '../common/ConfirmModal';

const LOCKED_STATUSES = ['shipped', 'delivered', 'cancelled'];

const toInputValue = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const formatDisplay = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
};

const mapError = (err, t) => {
  const code = err?.response?.data?.code;
  if (code === 'DELIVERY_DATE_PAST') return t('delivery.confirmed.errorPast');
  if (code === 'DELIVERY_DATE_LOCKED') return t('delivery.confirmed.errorLocked');
  return err?.response?.data?.message || t('delivery.confirmed.errorGeneric', 'Something went wrong');
};

const ConfirmDeliveryDate = ({ order, mode = 'business', onChange }) => {
  const { t } = useTranslation('orders');
  const [editing, setEditing] = useState(false);
  const [pickerValue, setPickerValue] = useState(toInputValue(order?.delivery?.confirmedDate));
  const [noteValue, setNoteValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearNote, setClearNote] = useState('');

  const confirmedDate = order?.delivery?.confirmedDate;
  const scheduledFor = order?.delivery?.scheduledFor;
  const status = order?.status;
  const isLocked = LOCKED_STATUSES.includes(status);

  // Find the most recent note from dateHistory
  const dateHistory = order?.delivery?.dateHistory;
  const lastHistoryEntry = dateHistory?.length ? dateHistory[dateHistory.length - 1] : null;
  const lastNote = lastHistoryEntry?.note || null;

  if (mode === 'business') {
    if (status === 'cancelled') return null;
    if (!confirmedDate && !scheduledFor) return null;

    return (
      <div className="space-y-1">
        {confirmedDate ? (
          <>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span className="text-base font-bold text-gray-900">
                {t('delivery.confirmed.label')}: {formatDisplay(confirmedDate)}
              </span>
            </div>
            {lastNote && (
              <div className="text-xs text-gray-500 ml-6 italic">
                {lastNote}
              </div>
            )}
            {scheduledFor && new Date(scheduledFor).toDateString() !== new Date(confirmedDate).toDateString() && (
              <div className="text-xs text-gray-500 ml-6">
                {t('delivery.confirmed.requestedLabel')}: {formatDisplay(scheduledFor)}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span>{t('delivery.confirmed.requestedLabel')}: {formatDisplay(scheduledFor)}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500 ml-6 italic">
              <Clock className="w-3 h-3" />
              {t('delivery.confirmed.awaitingConfirmation')}
            </div>
          </>
        )}
      </div>
    );
  }

  // Wholesaler mode
  const openEditor = () => {
    setPickerValue(toInputValue(confirmedDate));
    setNoteValue('');
    setEditing(true);
  };

  const save = async () => {
    if (!pickerValue) {
      toast.error(t('delivery.confirmed.placeholder'));
      return;
    }
    setSaving(true);
    try {
      const iso = new Date(pickerValue).toISOString();
      const { data } = await ordersAPI.setConfirmedDeliveryDate(order._id, {
        confirmedDate: iso,
        note: noteValue || undefined
      });
      toast.success(confirmedDate ? t('delivery.confirmed.successUpdated') : t('delivery.confirmed.successSet'));
      setEditing(false);
      onChange?.(data.order);
    } catch (err) {
      toast.error(mapError(err, t));
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setEditing(false);
    setNoteValue('');
  };

  const confirmClear = async () => {
    setSaving(true);
    try {
      const { data } = await ordersAPI.clearConfirmedDeliveryDate(order._id, { note: clearNote || undefined });
      toast.success(t('delivery.confirmed.successCleared'));
      setClearOpen(false);
      setClearNote('');
      onChange?.(data.order);
    } catch (err) {
      toast.error(mapError(err, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-blue-600" />
          <span className="text-sm text-gray-600">{t('delivery.confirmed.label')}:</span>
          {confirmedDate ? (
            <span className="text-base font-bold text-gray-900">{formatDisplay(confirmedDate)}</span>
          ) : (
            <span className="text-sm text-gray-400 italic">{t('delivery.confirmed.notSetYet')}</span>
          )}
        </div>
        {!isLocked && !editing && (
          <div className="flex items-center gap-1">
            <button
              onClick={openEditor}
              className="text-xs px-2 py-1 rounded text-blue-600 hover:bg-blue-50 flex items-center gap-1"
              type="button"
            >
              <Pencil className="w-3 h-3" />
              {confirmedDate ? t('delivery.confirmed.editButton') : t('delivery.confirmed.setButton')}
            </button>
            {confirmedDate && (
              <button
                onClick={() => { setClearNote(''); setClearOpen(true); }}
                className="text-xs px-2 py-1 rounded text-red-600 hover:bg-red-50 flex items-center gap-1"
                type="button"
              >
                <X className="w-3 h-3" />
                {t('delivery.confirmed.clearButton')}
              </button>
            )}
          </div>
        )}
      </div>

      {editing && (
        <div className="p-3 bg-blue-50 rounded-lg space-y-2">
          <input
            type="date"
            value={pickerValue}
            onChange={(e) => setPickerValue(e.target.value)}
            onClick={(e) => e.target.showPicker?.()}
            onFocus={(e) => e.target.showPicker?.()}
            className="w-full border border-blue-200 rounded px-2 py-1 text-sm cursor-pointer"
          />
          <textarea
            value={noteValue}
            onChange={(e) => setNoteValue(e.target.value.slice(0, 200))}
            placeholder={t('delivery.confirmed.notePlaceholder', 'Add a note (optional)')}
            className="w-full border border-blue-200 rounded px-2 py-1 text-xs"
            rows={2}
          />
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={cancelEdit}
              type="button"
              className="text-xs px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700"
              disabled={saving}
            >
              {t('delivery.confirmed.cancelButton', 'Cancel')}
            </button>
            <button
              onClick={save}
              type="button"
              className="text-xs px-3 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
              disabled={saving || !pickerValue}
            >
              {t('delivery.confirmed.saveButton', 'Save')}
            </button>
          </div>
        </div>
      )}

      {confirmedDate && lastNote && !editing && (
        <div className="text-xs text-gray-500 ml-6 italic">
          {lastNote}
        </div>
      )}

      {scheduledFor && confirmedDate &&
       new Date(scheduledFor).toDateString() !== new Date(confirmedDate).toDateString() && (
        <div className="text-xs text-gray-500 ml-6">
          {t('delivery.confirmed.requestedLabel')}: {formatDisplay(scheduledFor)}
        </div>
      )}

      <ConfirmModal
        isOpen={clearOpen}
        title={t('delivery.confirmed.clearConfirmTitle')}
        message={t('delivery.confirmed.clearConfirmBody')}
        icon="warning"
        confirmLabel={t('delivery.confirmed.clearButton')}
        confirmColor="red"
        onConfirm={confirmClear}
        onCancel={() => setClearOpen(false)}
      >
        <textarea
          value={clearNote}
          onChange={(e) => setClearNote(e.target.value.slice(0, 200))}
          placeholder={t('delivery.confirmed.clearNotePlaceholder')}
          className="w-full border border-gray-200 rounded px-2 py-1 text-xs"
          rows={2}
        />
      </ConfirmModal>
    </div>
  );
};

export default ConfirmDeliveryDate;
